import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "./DashboardLayout";
import { api, formatDateMX } from "../../lib/apiClient";
import { statusMeta } from "../../lib/constants";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAdminRealtime } from "../../hooks/useAdminRealtime";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DIAS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -d.getDay());

export default function Calendario() {
  const [ref, setRef] = useState(new Date());
  const [view, setView] = useState("mes");
  const [data, setData] = useState({ orders: [], events: [] });
  const [selDay, setSelDay] = useState(null);
  const navigate = useNavigate();

  const range = useMemo(() => {
    if (view === "mes") {
      const s = new Date(ref.getFullYear(), ref.getMonth(), 1);
      const e = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
      return [s, e];
    }
    if (view === "semana") { const s = startOfWeek(ref); return [s, addDays(s, 6)]; }
    return [ref, ref];
  }, [ref, view]);

  const load = React.useCallback(() => api.get("/calendar", { params: { start: iso(range[0]), end: iso(range[1]) } }).then((r) => setData(r.data)).catch(() => {}), [range]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);
  useAdminRealtime(load, ["orders", "events", "availability"]);

  const ordersFor = (dstr) => data.orders.filter((o) => o.delivery_date === dstr).sort((a, b) => (a.delivery_time || "").localeCompare(b.delivery_time || ""));

  const shift = (n) => setRef(view === "mes" ? new Date(ref.getFullYear(), ref.getMonth() + n, 1) : addDays(ref, view === "semana" ? n * 7 : n));

  const title = view === "mes" ? `${MESES[ref.getMonth()]} ${ref.getFullYear()}`
    : view === "semana" ? `${formatDateMX(iso(range[0]))} — ${formatDateMX(iso(range[1]))}`
    : formatDateMX(iso(ref));

  return (
    <DashboardLayout title="Calendario">
      <div className="grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center sm:justify-between gap-3 mb-5 min-w-0">
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:flex sm:justify-start sm:gap-3 min-w-0">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="p-2 rounded-full bg-blancoCalido border border-beige text-vino"><ChevronLeft /></button>
          <h2 className="font-serif text-lg sm:text-2xl text-vino min-w-0 sm:min-w-[180px] text-center truncate">{title}</h2>
          <button onClick={() => shift(1)} data-testid="cal-next" className="p-2 rounded-full bg-blancoCalido border border-beige text-vino"><ChevronRight /></button>
        </div>
        <div className="flex bg-blancoCalido border border-beige rounded-full p-1 justify-self-center sm:justify-self-auto">
          {[["mes", "Mes"], ["semana", "Semana"], ["dia", "Día"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
              className={`px-4 py-1.5 rounded-full text-sm transition-all ${view === v ? "bg-vino text-crema" : "text-cafe/60"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* MONTH */}
      {view === "mes" && (() => {
        const year = ref.getFullYear(), month = ref.getMonth();
        const startDay = new Date(year, month, 1).getDay();
        const days = new Date(year, month + 1, 0).getDate();
        return (
          <div className="grid grid-cols-7 gap-1 sm:gap-2 min-w-0">
            {DIAS.map((d) => <div key={d} className="text-center text-xs text-cafe/50 font-medium py-1">{d}</div>)}
            {Array.from({ length: startDay }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: days }).map((_, i) => {
              const dstr = iso(new Date(year, month, i + 1)); const os = ordersFor(dstr); const sat = os.length >= 6;
              return (
                <button key={i} onClick={() => setSelDay(dstr)} data-testid={`cal-day-${i + 1}`}
                  className={`min-w-0 aspect-square rounded-xl sm:rounded-2xl border p-1 sm:p-2 flex flex-col items-start transition-all ${sat ? "bg-rosa/20 border-rosa" : "bg-blancoCalido border-beige"} hover:border-vino`}>
                  <span className="text-xs text-cafe/70">{i + 1}</span>
                  <div className="flex flex-wrap gap-0.5 mt-auto">{os.slice(0, 4).map((o) => <span key={o.id} className="w-1.5 h-1.5 rounded-full" style={{ background: statusMeta(o.status).color }} />)}</div>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* WEEK */}
      {view === "semana" && (
        <div className="grid grid-cols-1 min-[380px]:grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {Array.from({ length: 7 }).map((_, i) => {
            const d = addDays(range[0], i); const dstr = iso(d); const os = ordersFor(dstr);
            return (
              <div key={i} className="bg-blancoCalido border border-beige rounded-[20px] p-3 min-h-[160px]">
                <p className="text-center text-xs text-vino font-medium border-b border-beige pb-2 mb-2">{DIAS[d.getDay()]} {d.getDate()}</p>
                <div className="space-y-2">
                  {os.map((o) => (
                    <button key={o.id} onClick={() => navigate(`/admin/pedidos?open=${o.id}`)} data-testid={`week-order-${o.id}`}
                      className="w-full text-left text-xs bg-crema rounded-xl p-2 border-l-4 hover:shadow-sm" style={{ borderColor: statusMeta(o.status).color }}>
                      <p className="font-medium text-vino">{o.delivery_time || "-"}</p>
                      <p className="text-cafe/70 line-clamp-1">{o.customer_name}</p>
                      <p className="text-cafe/50 line-clamp-1">{o.product_name}</p>
                    </button>
                  ))}
                  {os.length === 0 && <p className="text-center text-cafe/30 text-xs pt-4">—</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DAY */}
      {view === "dia" && (() => {
        const os = ordersFor(iso(ref));
        return (
          <div className="max-w-2xl">
            {os.length === 0 ? <p className="text-cafe/50 text-sm bg-blancoCalido border border-beige rounded-[20px] p-6 text-center">Sin pedidos este día 🌸</p> : (
              <div className="space-y-3">
                {os.map((o) => {
                  const m = statusMeta(o.status);
                  return (
                    <button key={o.id} onClick={() => navigate(`/admin/pedidos?open=${o.id}`)} data-testid={`day-order-${o.id}`}
                      className="w-full text-left bg-blancoCalido border border-beige rounded-[20px] p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:shadow-pastel border-l-4" style={{ borderLeftColor: m.color }}>
                      <div className="text-center min-w-[60px]"><p className="font-serif text-2xl text-vino">{(o.delivery_time || "--:--").slice(0, 5)}</p></div>
                      <div className="flex-1"><p className="font-medium text-cafe">{o.customer_name}</p><p className="text-sm text-cafe/60">{o.product_name} · {o.folio}</p></div>
                      <span className="text-xs px-2.5 py-1 rounded-full text-white" style={{ background: m.color }}>{m.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Month day modal */}
      {selDay && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="cal-day-modal">
          <div className="absolute inset-0 bg-vinoDark/30 backdrop-blur-sm" onClick={() => setSelDay(null)} />
          <div className="relative bg-crema rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-4 sm:p-6 max-h-[80vh] overflow-y-auto overflow-x-hidden">
            <h3 className="font-serif text-2xl text-vino mb-3">{formatDateMX(selDay)}</h3>
            {ordersFor(selDay).length === 0 ? <p className="text-cafe/50 text-sm">Sin pedidos este día.</p> : (
              <div className="space-y-2">
                {ordersFor(selDay).map((o) => (
                  <button key={o.id} onClick={() => navigate(`/admin/pedidos?open=${o.id}`)} className="w-full text-left bg-blancoCalido border border-beige rounded-2xl p-3">
                    <div className="flex justify-between gap-2 min-w-0"><span className="font-medium text-vino text-sm truncate">{o.folio}</span><span className="shrink-0 text-xs px-2 py-0.5 rounded-full text-white" style={{ background: statusMeta(o.status).color }}>{statusMeta(o.status).label}</span></div>
                    <p className="text-sm text-cafe/70 break-words">{o.delivery_time || "-"} · {o.customer_name} · {o.product_name}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
